import { useEffect, useRef, useState } from "react";
import {
  Sparkles, Target, Rocket, Flame, Award, Trophy, CheckCircle2, Zap,
  TrendingUp, CalendarDays, Star, PartyPopper, ArrowUpRight
} from "lucide-react";

const BADGE_ICONS = { Sparkles, Target, Rocket, Flame, Award, Trophy };
const card = "rounded-[var(--radius)] border border-[var(--border-soft)] bg-white shadow-[var(--shadow-sm)]";

/** Compteur animé (transform/opacity + valeur), respecte prefers-reduced-motion. */
export function useCountUp(target, duration = 800) {
  const [val, setVal] = useState(0);
  const raf = useRef(null);
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(target); return; }
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      setVal(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => raf.current && cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return val;
}

/** Anneau de progression SVG (objectif du jour). */
function DailyRing({ count, target, color }) {
  const pct = Math.min(1, target ? count / target : 0);
  const anim = useCountUp(pct, 900);
  const r = 58, C = 2 * Math.PI * r;
  const hit = count >= target;
  const ringColor = hit ? "var(--affinity-strong)" : color;
  const animatedCount = Math.round(useCountUp(count, 700));
  return (
    <div className="relative grid h-[150px] w-[150px] place-items-center" data-testid="daily-ring">
      <svg width="150" height="150" className="-rotate-90">
        <circle cx="75" cy="75" r={r} fill="none" stroke="var(--surface-2)" strokeWidth="12" />
        <circle cx="75" cy="75" r={r} fill="none" stroke={ringColor} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C} strokeDashoffset={C * (1 - anim)}
          style={{ transition: "stroke 200ms ease" }} />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <div className="text-4xl font-extrabold tabular-nums leading-none" style={{ color: ringColor }}>
            {animatedCount}<span className="text-lg text-muted-ink">/{target}</span>
          </div>
          <div className="mx-auto mt-0.5 max-w-[84px] text-[9px] font-semibold uppercase leading-[1.15] tracking-tight text-muted-ink">spots aujourd'hui</div>
        </div>
      </div>
      {hit && (
        <span className="absolute -right-1 -top-1 grid h-8 w-8 place-items-center rounded-full bg-[var(--affinity-strong)] text-white shadow-md">
          <CheckCircle2 className="h-5 w-5" />
        </span>
      )}
    </div>
  );
}

function motivation(today, streak, target) {
  const { count, remaining, hit } = today;
  if (hit) {
    return {
      tone: "success",
      title: `Bravo, objectif atteint ! 🎉`,
      text: count > target
        ? `Excellent — ${count} spots bookés aujourd'hui, tu dépasses l'objectif !`
        : `${count} spots bookés aujourd'hui. Continue sur ta lancée${streak > 1 ? ` — série de ${streak} jours 🔥` : ""} !`,
    };
  }
  if (count === 0) {
    return {
      tone: "neutral",
      title: "En route vers l'objectif du jour 🎯",
      text: `Aucun spot booké pour l'instant. Objectif : ${target} spots aujourd'hui. C'est parti !`,
    };
  }
  return {
    tone: "warn",
    title: `Plus que ${remaining} spot${remaining > 1 ? "s" : ""} !`,
    text: `Tu y es presque : encore ${remaining} spot${remaining > 1 ? "s" : ""} pour valider ta journée. 💪`,
  };
}

const TONE = {
  success: { bg: "rgba(31,157,85,0.08)", border: "rgba(31,157,85,0.28)", color: "#146B3A", Icon: PartyPopper },
  warn: { bg: "rgba(238,114,46,0.08)", border: "rgba(238,114,46,0.3)", color: "#8A3A10", Icon: Zap },
  neutral: { bg: "var(--surface-2)", border: "var(--border-soft)", color: "var(--shariti-ink)", Icon: Target },
};

/** Tableau de bord gamifié complet à partir d'un payload de stats CDP. */
export function CdpObjectiveDashboard({ data, compact = false }) {
  if (!data) return null;
  const { today, week, current_streak, best_streak, totals, badges, target } = data;
  const color = data.cdp?.color || "var(--shariti-orange)";
  const mot = motivation(today, current_streak, target);
  const Tone = TONE[mot.tone];
  const maxWeek = Math.max(target, ...week.map((w) => w.count), 1);

  return (
    <div className="space-y-4" data-testid="cdp-objective-dashboard">
      {/* Hero : objectif du jour + message motivant */}
      <div className={`${card} overflow-hidden`}>
        <div className="flex flex-col items-center gap-5 p-5 sm:flex-row sm:items-center">
          <DailyRing count={today.count} target={target} color={color} />
          <div className="min-w-0 flex-1">
            <div className="mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-bold"
              style={{ background: Tone.bg, borderColor: Tone.border, color: Tone.color, borderWidth: 1 }}
              data-testid="daily-motivation">
              <Tone.Icon className="h-4 w-4" /> {mot.title}
            </div>
            <p className="text-sm text-muted-ink">{mot.text}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <StreakPill icon={Flame} label="Série en cours" value={`${current_streak} j`} active={current_streak > 0} />
              <StreakPill icon={Trophy} label="Meilleure série" value={`${best_streak} j`} />
              <StreakPill icon={Star} label="Objectif" value={`${target}/jour`} />
            </div>
          </div>
        </div>
      </div>

      {/* Semaine : barres par jour */}
      <div className={`${card} p-4`} data-testid="cdp-week-strip">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-1.5 text-sm font-bold"><CalendarDays className="h-4 w-4 text-[var(--shariti-orange)]" />Ma semaine</h3>
          <span className="text-xs text-muted-ink">{data.week_total} spot(s) bookés</span>
        </div>
        <div className="grid grid-cols-6 gap-2">
          {week.map((w) => {
            const h = Math.max(6, Math.round(64 * (w.count / maxWeek)));
            const barColor = w.hit ? "var(--affinity-strong)" : w.count > 0 ? "var(--shariti-orange)" : "var(--border-soft)";
            return (
              <div key={w.date} className={`flex flex-col items-center rounded-lg p-1.5 ${w.is_today ? "bg-[rgba(238,114,46,0.06)] ring-1 ring-[rgba(238,114,46,0.3)]" : ""}`}
                data-testid={`cdp-day-${w.weekday}`}>
                <div className="flex h-[72px] items-end">
                  <div className="w-6 rounded-t-md transition-[height] duration-500 ease-out" style={{ height: `${h}px`, background: barColor }} title={`${w.count} spot(s)`} />
                </div>
                <div className="mt-1 text-sm font-bold tabular-nums leading-none">{w.count}</div>
                <div className="text-[10px] font-semibold uppercase text-muted-ink">{w.weekday}</div>
                {w.hit ? <CheckCircle2 className="mt-0.5 h-3 w-3 text-[var(--affinity-strong)]" />
                  : w.active ? <span className="mt-0.5 h-3 w-3" /> : <span className="mt-0.5 h-3 w-3" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats + Badges */}
      <div className={`grid gap-4 ${compact ? "" : "md:grid-cols-2"}`}>
        <div className={`${card} p-4`}>
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold"><TrendingUp className="h-4 w-4 text-[var(--shariti-orange)]" />Mes statistiques</h3>
          <div className="grid grid-cols-2 gap-2">
            <Stat label="Spots bookés (total)" value={totals.total_spots} />
            <Stat label="Jours à l'objectif" value={`${totals.days_goal_hit}/${totals.active_days}`} />
            <Stat label="Taux de réussite" value={`${totals.success_rate}%`} accent={totals.success_rate >= 70} />
            <Stat label="Moy. / jour actif" value={totals.avg_per_active_day} />
            <Stat label="Meilleure journée" value={totals.best_day} />
            <Stat label="Jours actifs" value={totals.active_days} />
          </div>
        </div>
        <div className={`${card} p-4`} data-testid="cdp-badges">
          <h3 className="mb-3 flex items-center gap-1.5 text-sm font-bold"><Award className="h-4 w-4 text-[var(--shariti-orange)]" />Mes trophées</h3>
          <div className="grid grid-cols-3 gap-2">
            {badges.map((b) => {
              const Icon = BADGE_ICONS[b.icon] || Star;
              return (
                <div key={b.id} title={b.desc}
                  data-testid={`badge-${b.id}`}
                  className={`flex flex-col items-center rounded-xl border p-2 text-center transition-transform duration-150 ${b.earned ? "border-[rgba(238,114,46,0.35)] bg-[rgba(238,114,46,0.06)] hover:-translate-y-0.5" : "border-[var(--border-soft)] bg-[var(--surface-2)] opacity-55 grayscale"}`}>
                  <span className={`grid h-9 w-9 place-items-center rounded-full ${b.earned ? "bg-[var(--shariti-orange)] text-white" : "bg-white text-muted-ink"}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="mt-1 text-[11px] font-bold leading-tight">{b.label}</div>
                  <div className="text-[9px] leading-tight text-muted-ink">{b.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const StreakPill = ({ icon: Icon, label, value, active }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${active ? "border-[rgba(238,114,46,0.4)] bg-[rgba(238,114,46,0.08)] text-[#8A3A10]" : "border-[var(--border-soft)] bg-white text-muted-ink"}`}>
    <Icon className={`h-3.5 w-3.5 ${active ? "text-[var(--shariti-orange)]" : ""}`} />
    <span className="text-ink">{value}</span><span className="hidden sm:inline">· {label}</span>
  </span>
);

const Stat = ({ label, value, accent }) => (
  <div className="rounded-lg border border-[var(--border-soft)] bg-[var(--surface-2)] p-2.5">
    <div className="text-xl font-extrabold tabular-nums" style={{ color: accent ? "var(--affinity-strong)" : "var(--shariti-ink)" }}>{value}</div>
    <div className="text-[10px] font-semibold uppercase leading-tight text-muted-ink">{label}</div>
  </div>
);
